import { useLocation, useNavigate } from "react-router-dom";
import { URLdecode, URLencode } from "../utils/url";
import { customerConfig } from "../customerConfig";
import { templateCustomerData } from "../data/order/customerTableData";
import axios from "axios";
import getAccounts from "../controllers/user/getAccounts";
import { useRef } from "react";
import fetchServer from "../controllers/fetchServer";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import getSubscription from "../controllers/subscription/getSubscription";

export default function useCustomers() {
  const params = URLdecode();
  const navigate = useNavigate();
  const location = useLocation();
  const cancelCustomersToken = useRef(null);
  const cancelStatToken = useRef(null);

  const filterTabs = () => {
    const tabs = [
      {
        label: customerConfig.filterTabs.all.label,
        value: customerConfig.filterTabs.all.code,
        amount: 130,
        onClick: () => {
          navigate(
            `${location.pathname}?${URLencode({
              ...params,
              tab: customerConfig.filterTabs.all.code,
            })}`,
          );
        },
      },
      {
        label: customerConfig.filterTabs.verified.label,
        value: customerConfig.filterTabs.verified.code,
        amount: 130,
        onClick: () => {
          navigate(
            `${location.pathname}?${URLencode({
              ...params,
              tab: customerConfig.filterTabs.verified.code,
            })}`,
          );
        },
      },
      {
        label: customerConfig.filterTabs.awaitingVerification.label,
        value: customerConfig.filterTabs.awaitingVerification.code,
        amount: 130,
        onClick: () => {
          navigate(
            `${location.pathname}?${URLencode({
              ...params,
              tab: customerConfig.filterTabs.awaitingVerification.code,
            })}`,
          );
        },
      },
      {
        label: customerConfig.filterTabs.awaitingDocuments.label,
        value: customerConfig.filterTabs.awaitingDocuments.code,
        amount: 130,
        onClick: () => {
          navigate(
            `${location.pathname}?${URLencode({
              ...params,
              tab: customerConfig.filterTabs.awaitingDocuments.code,
            })}`,
          );
        },
      },
      {
        label: customerConfig.filterTabs.notSubmitted.label,
        value: customerConfig.filterTabs.notSubmitted.code,
        amount: 130,
        onClick: () => {
          navigate(
            `${location.pathname}?${URLencode({
              ...params,
              tab: customerConfig.filterTabs.notSubmitted.code,
            })}`,
          );
        },
      },
      {
        label: customerConfig.filterTabs.deactivated.label,
        value: customerConfig.filterTabs.deactivated.code,
        amount: 130,
        onClick: () => {
          navigate(
            `${location.pathname}?${URLencode({
              ...params,
              tab: customerConfig.filterTabs.deactivated.code,
            })}`,
          );
        },
      },
    ];
    return tabs;
  };

  const getCustomers = async ({
    setLoading,
    limit,
    page,
    search,
    tab,
    orderBy,
    order,
  }) => {
    setLoading?.(true);
    let cancellationMessage = "Operation canceled due to new request.";
    if (cancelCustomersToken.current) {
      cancelCustomersToken.current.cancel(cancellationMessage);
    }

    cancelCustomersToken.current = axios.CancelToken.source();
    const limit1 = limit || 1;
    const params = {
      // populate: ["detail", "accountManager"],
      limit: limit1,
      offset: ((page || 1) - 1) * limit1,
    };
    let agency = false;
    if (orderBy) {
      params.orderBy = orderBy;
      params.order = order;
    }
    if (tab === customerConfig.filterTabs.deactivated.code) {
      params.filterBy = "accountStatus";
      params.filterValue = "Deactivated";
    }
    if (tab === customerConfig.filterTabs.verified.code) {
      params.filterBy = "detail.isVerified,detail.requestedVerification";
      params.filterValue = "true,false";
      agency = true;
    }
    if (tab === customerConfig.filterTabs.awaitingVerification.code) {
      params.filterBy = "detail.isVerified,detail.requestedVerification";
      params.filterValue = "false,true";
      agency = true;
    }
    if (tab === customerConfig.filterTabs.notSubmitted.code) {
      params.filterBy = "detail.requestedVerification,detail.isVerified";
      params.filterValue = `false,false`;
      agency = true;
    }
    if (tab === customerConfig.filterTabs.awaitingDocuments.code) {
      params.filterBy =
        "detail.CACCertificateDoc,detail.CACCO2Doc,detail.CACC07Doc,detail.identityVerificationDoc,detail.requestedVerification";
      params.filterValue = `null,null,null,null,true`;
      agency = true;
    }
    if (search) {
      params.searchBy = "firstName,lastName,email,phone";
      params.keyword = search;
    }
    let data = [];
    let total = 10;
    // console.log("accounts");
    await getAccounts(
      new URLSearchParams(params)?.toString(),
      cancelCustomersToken.current.token,
      agency,
    ).then((res) => {
      if (!res?.cancelled) setLoading?.(false);
      if (res?.return === 1) {
        data = res?.data?.data;
        total = res?.data?.meta?.total;
      }
    });
    let filteredData = data || [];
    const templated = filteredData.map((item) => templateCustomerData(item));
    return [total, templated, filteredData];
  };

  const getCustomerStatistics = async ({
    setLoading,
    id,
    callback,
    dateFilter,
  }) => {
    const { range, date } = dateFilter || {};

    // fallback to today if date is missing/invalid
    const baseDate = moment(date, "M/D/YYYY", true).isValid()
      ? moment(date, "M/D/YYYY")
      : moment();

    // always set endDate from baseDate
    const endDate = baseDate.format("YYYY-MM-DD");

    let startDate;
    switch (range) {
      case "week":
        startDate = baseDate.clone().subtract(1, "week").format("YYYY-MM-DD");
        break;
      case "month":
        startDate = baseDate.clone().subtract(1, "month").format("YYYY-MM-DD");
        break;
      case "year":
        startDate = baseDate.clone().subtract(1, "year").format("YYYY-MM-DD");
        break;
      case "All":
        startDate = "2022-05-31";
        break;
      default:
        // default to one week before if no range given
        startDate = baseDate.clone().subtract(1, "week").format("YYYY-MM-DD");
        break;
    }
    setLoading?.(true);
    let cancellationMessage = "Operation canceled due to new request.";
    if (cancelStatToken.current) {
      cancelStatToken.current.cancel(cancellationMessage);
    }

    cancelStatToken.current = axios.CancelToken.source();

    await fetchServer({
      method: "GET",
      url: `/product/v1/statistics/agent/${id}?startDate=${startDate}&endDate=${endDate}`,
      cancelToken: cancelStatToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading?.(false);
        if (res) {
          if (res.status === 200) {
            const data = res?.data?.data;
            const flight = {
              count: data?.flightsSold?.total || 0,
              amount: data?.flightsSold?.totalAmount || 0,
            };
            const stay = {
              count: data?.accommodationsSold?.total || 0,
              amount: data?.accommodationsSold?.totalAmount || 0,
            };
            const tour = {
              count: data?.toursSold?.total || 0,
              amount: data?.toursSold?.totalAmount || 0,
            };
            const insurance = {
              count: data?.insuranceSold?.total || 0,
              amount: data?.insuranceSold?.totalAmount || 0,
            };
            const dataArray = [flight, stay, tour, insurance];
            const total = {
              count: dataArray?.reduce((acc, current) => {
                return acc + (current?.count || 0);
              }, 0),
              amount: dataArray?.reduce((acc, current) => {
                return acc + (current?.amount || 0);
              }, 0),
            };
            callback?.({ flight, stay, tour, insurance, total });
          }
        }
      })
      .catch((err) => {
        // console.log(err.message, err);
        // if (err.message === "Network Error!") {
        //   result["msg"] = "Network Error!";
        //   result["error"] = "Please check your connection.";
        // }
      });
  };

  const getCustomerWallet = async ({ id, setLoading, callback }) => {
    let result = { return: 0, msg: "Something went wrong fetching wallet!" };
    setLoading?.(true);

    await fetchServer({ method: "GET", url: `/payment/v1/wallet/other/${id}` })
      .then((res) => {
        if (!res?.cancelled) setLoading?.(false);
        if (res?.data && !res?.data?.error) {
          const data = res?.data?.data;
          callback?.(data);

          result = { return: 1, msg: "Successful", data };
        } else if (res?.data?.error) result["msg"] = res.data.error;
      })
      .catch((err) => {
        console.log("Network Error!");
      });
  };

  const getAccountStatement = async ({
    setLoading,
    startDate,
    endDate,
    callback,
    id,
  }) => {
    const params = URLdecode();

    setLoading && setLoading(true);

    await fetchServer({
      method: "GET",
      url: `/payment/v2/transaction/transactionReport/${id}?startDate=${moment(
        startDate,
      ).format("YYYY-MM-DD")}&endDate=${moment(endDate).format("YYYY-MM-DD")}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            callback?.(res?.data?.data);
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                res?.data?.error ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading && setLoading(false);
      });
  };

  const getCustomerSubscription = async ({ setLoading, id, callback }) => {
    setLoading?.(true);
    const res = await getSubscription(id).finally(() => setLoading?.(false));
    if (res?.return) {
      callback?.(res?.data);
    }
  };

  return {
    filterTabs,
    getCustomers,
    getCustomerStatistics,
    getCustomerWallet,
    getAccountStatement,
    getCustomerSubscription,
  };
}
